import * as mixpanelBrowser from 'mixpanel-browser';
import {
  createContext,
  type ProviderProps,
  useMemo,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { useMount } from 'react-use';
import { useGetConsents } from 'api';
import { usePlatform } from 'hooks';
import { environment } from 'utils/environmentVariables';
import { useSession } from './Session';

type MixpanelContext = mixpanelBrowser.Mixpanel | undefined;
type UseMixpanelReturnType = {
  mixpanel: mixpanelBrowser.Mixpanel;
  mixpanelTrackEvent: (
    eventName: string,
    properties?: mixpanelBrowser.Dict
  ) => void;
};

export const MIXPANEL_TOKEN = environment.REACT_APP_MIXPANEL_TOKEN;

// Custom options to pass to `mixpanel.init()`.
export const MIXPANEL_CONFIG = {
  api_host: 'https://api-eu.mixpanel.com',
  track_pageview: false,
  ignore_dnt: true,
  opt_out_tracking_by_default: false,
  debug: false,
};

export const mixpanelContext = createContext<MixpanelContext>(undefined);

export interface MixpanelProviderProps
  extends Omit<ProviderProps<MixpanelContext>, 'value'> {
  token?: string;
  config?: Partial<mixpanelBrowser.Config>;
  name?: string;
}

export function MixpanelProvider({
  token = MIXPANEL_TOKEN,
  config: _config = MIXPANEL_CONFIG,
  name: _name,
  children,
}: MixpanelProviderProps) {
  // Name is used to identify the mixpanel instance in the global scope
  const name = useMemo(() => _name ?? 'app-mixpanel-browser', [_name]);

  const config = useMemo(
    () => ({
      track_pageview: false, // Rarely makes sense to track page views in React apps
      ..._config,
    }),
    [_config]
  );

  // If `token` is `undefined` or otherwise falsey then `useMixpanel()` will return
  // `undefined` which can be useful for environments where Mixpanel integration is not desired.
  const context = useMemo(
    () => (token ? mixpanelBrowser.init(token, config, name) : undefined),
    [config, name, token]
  );

  return (
    <mixpanelContext.Provider value={context}>
      {children}
    </mixpanelContext.Provider>
  );
}

type Product = 'TopUp' | 'App' | 'Processing';
export type MixpanelTrackEvent = UseMixpanelReturnType['mixpanelTrackEvent'];
export const useMixpanel = (
  superProperties: {
    Page: string;
    Product?: Product;
    onMountProps?: Record<string, any>;
  } & Record<string, any>
): UseMixpanelReturnType => {
  const mixpanel = useContext(mixpanelContext);

  if (mixpanel === undefined) {
    throw new Error('useMixpanel must be used within a MixpanelProvider');
  }
  const session = useSession();
  const { data } = useGetConsents(!!session);
  const { platform } = usePlatform();

  useEffect(() => {
    if (data) {
      if (!data.userConsents['DATA_ANALYSIS']) {
        mixpanel.opt_out_tracking();
      } else if (!mixpanel.has_opted_in_tracking()) {
        mixpanel.opt_in_tracking();
      }
    }
  }, [data, mixpanel]);

  const mixpanelTrackEvent: MixpanelTrackEvent = useCallback(
    (eventName, properties) => {
      mixpanel.track(eventName, properties);
    },
    [mixpanel]
  );

  useMount(() => {
    if (superProperties) {
      const { onMountProps, ...rest } = superProperties;
      mixpanel.register(rest);
      mixpanelTrackEvent(`Page ${superProperties.Page} mounted`, {
        ...onMountProps,
        platform: platform,
      });
    }
  });

  return { mixpanel, mixpanelTrackEvent };
};
